// ============================================================
// Canonical list of the dynamic-field tokens a design may contain.
// Single source of truth for their user-facing labels — the canvas
// chips, the toolbar's insert menu, and the setup wizard all label
// fields from here so the same field never goes by two names.
// Resolution semantics live in @rh/shared/templates/design-tokens.
// ============================================================

export type TokenScope = 'agency' | 'recruiter'

export interface TokenField {
  /** Canonical literal, e.g. '{{recruiter.name}}'. */
  token: string
  scope: TokenScope
  /** Short label rendered inside canvas chips and menus. */
  label: string
  /** One-line tooltip explaining where the value comes from. */
  hint: string
}

const recruiterHint = 'Each recruiter’s own — fills in automatically'
const agencyHint = 'From your Agency profile — stays in sync'

export const TOKEN_FIELDS: TokenField[] = [
  {
    token: '{{recruiter.name}}',
    scope: 'recruiter',
    label: 'Recruiter’s name',
    hint: recruiterHint,
  },
  {
    token: '{{recruiter.phone}}',
    scope: 'recruiter',
    label: 'Recruiter’s phone',
    hint: recruiterHint,
  },
  {
    token: '{{recruiter.email}}',
    scope: 'recruiter',
    label: 'Recruiter’s email',
    hint: recruiterHint,
  },
  {
    token: '{{recruiter.linkedIn}}',
    scope: 'recruiter',
    label: 'Recruiter’s LinkedIn',
    hint: recruiterHint,
  },
  {
    token: '{{agency.name}}',
    scope: 'agency',
    label: 'Agency name',
    hint: agencyHint,
  },
  {
    token: '{{agency.phone}}',
    scope: 'agency',
    label: 'Agency phone',
    hint: agencyHint,
  },
  {
    token: '{{agency.url}}',
    scope: 'agency',
    label: 'Agency website',
    hint: agencyHint,
  },
  {
    token: '{{agency.email}}',
    scope: 'agency',
    label: 'Agency email',
    hint: agencyHint,
  },
  {
    token: '{{agency.linkedIn}}',
    scope: 'agency',
    label: 'Agency LinkedIn',
    hint: agencyHint,
  },
]

const byToken = new Map(TOKEN_FIELDS.map(f => [f.token, f]))

/**
 * Looks a field up by token literal, tolerating the whitespace the
 * resolver tolerates (`{{ agency.phone }}` → the canonical field).
 */
export function tokenFieldByToken(token: string): TokenField | undefined {
  const exact = byToken.get(token)
  if (exact) return exact
  const m = token.match(/^\{\{\s*([a-zA-Z]+\.[a-zA-Z]+)\s*\}\}$/)
  return m ? byToken.get(`{{${m[1]}}}`) : undefined
}
